
import { Component } from 'vue-property-decorator';
import Export from '@/components/export/Export.vue';
import type { Project } from '@/models/project';
import { Getter } from 'vuex-class';
import { PrineConvertExportData } from '@/lib/prine/convert/types/PrineConvertExportData';
import moment from 'moment';
import { CombinedUser, getCombinedProjectUsers } from '@/helpers/getCombinedProjectUsers';
import { PrineConvert } from '@/lib/prine/convert/PrineConvert';
import { PrineConvertAdapterHTML } from '@/lib/prine/convert/adapters/PrineConvertAdapterHTML';
import type { Company } from '@/models/company';
import { PrineConvertAdapterCSV } from '@/lib/prine/convert/adapters/PrineConvertAdapterCSV';
import ExportLayout from '@/components/export/ExportLayout.vue';
import { AppliedLessonType } from '@/enums/AppliedLessonType';
import type { Worksheet } from '@/models/worksheet';
import { tradeWorksheetWebservice } from '@/webservices/tradeWorksheet';
import { pmAppliedLessonWebservice } from '@/webservices/pmappliedlesson';
import { appliedLessonWebservice } from '@/webservices/appliedlesson';
import type { AppliedLesson } from '@/models/appliedlesson';
import { showErrorNotification } from '@/helpers/showErrorNotification';
import { pmWorksheetWebservice } from '@/webservices/pmWorksheet';
import { xWorksheetWebservice } from '@/webservices/xworksheet';
import { xAppliedLessonWebservice } from '@/webservices/xappliedlesson';
import { projectWebservice } from '@/webservices/project';
import { ExportField } from '@/components/export/ExportField';
import { findCombinedUser } from '@/helpers/findCombinedUser';
import { DynamicField } from '@/models/dynamicField';

@Component({
    name: 'ExportWorksheetAppliedLessons',
    components: { ExportLayout },
})
export default class ExportWorksheetAppliedLessons extends Export {
    get fields(): ExportField[] {
        return [
            {
                field: 'insight',
                enabled: true,
                default: true,
                data: (data) => data.lesson.lesson.insight,
            },
            {
                field: 'todo',
                enabled: false,
                data: (data) => data.lesson.todo,
                width: '14%',
            },
            {
                field: 'solution',
                enabled: false,
                data: (data) => data.lesson.solution,
                width: '14%',
            },
            {
                field: 'responsible',
                enabled: false,
                data: (data) => {
                    const user = findCombinedUser(data.users, data.lesson);

                    if (user) {
                        return `${user.firstname} ${user.lastname}`;
                    }

                    return '-';
                },
                width: '110px',
            },
            {
                field: 'contacttill',
                enabled: false,
                data: (data) => (data.lesson.contacttill ? moment(data.lesson.contacttill).format('DD.MM.YYYY') : ''),
                width: '60px',
            },
            {
                field: 'done',
                enabled: true,
                default: true,
                data: (data) => data.lesson.done,
                ...Export.boolAttributes,
                width: '55px',
            },
            {
                field: 'needscheck',
                enabled: false,
                data: (data) => data.lesson.needscheck,
                ...Export.boolAttributes,
                width: '55px',
            },
            {
                field: 'checked',
                enabled: false,
                data: (data) => data.lesson.checked,
                ...Export.boolAttributes,
                width: '55px',
            },
            {
                field: 'checked_by_user',
                enabled: false,
                data: (data) =>
                    data.lesson.checked_by_user
                        ? `${data.lesson.checked_by_user.firstname} ${data.lesson.checked_by_user.lastname}`
                        : '',
                width: '110px',
            },
            {
                field: 'dynamic_field',
                enabled: false,
                data: (data) => (data.lesson.dynamic_field ? this.renderDynamicField(data.lesson.dynamic_field) : ''),
                width: '90px',
            },
            {
                field: 'cost',
                enabled: false,
                data: (data) => {
                    return data.lesson.cost == null
                        ? ''
                        : isNaN(Number(data.lesson.cost))
                        ? '-'
                        : Number(data.lesson.cost).toLocaleString('de-CH');
                },
                width: '80px',
            },
            {
                field: 'probability',
                enabled: false,
                data: (data) => {
                    const probability = Number(data.lesson.probability);
                    return data.lesson.probability == null
                        ? ''
                        : isNaN(probability)
                        ? '-'
                        : (probability / 100).toLocaleString('de-CH', {
                              style: 'percent',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                          });
                },
                width: '80px',
            },
            {
                field: 'impact',
                enabled: false,
                header: () => {
                    return this.$t('print.impact', {
                        divider: this.maxDynamicFieldValue(),
                    }) as string;
                },
                data: (data) => {
                    const impact = Number(data.lesson.impact);
                    return data.lesson.impact == null ? '' : isNaN(impact) ? '-' : impact.toLocaleString('de-CH');
                },
                width: '80px',
            },
        ];
    }

    private worksheetId: number;
    private type: string = AppliedLessonType.AppliedLesson;
    private onlyPR: boolean = false;
    private showResolved: boolean = false;

    private worksheet: Worksheet = {} as Worksheet;
    private applied_lessons = new Array<AppliedLesson>();

    private loaded = false;

    @Getter('company/dynamicFields') private dynamicFields: DynamicField[];
    @Getter('account/company') private company: Company;

    private project: Project = {} as Project;

    private mounted() {
        this.$watch(
            () => this.$route.query,
            () => {
                this.refreshParams();
            }
        );

        if (this.$route.query.projectId) {
            this.loadProject();
        }

        this.refreshParams();
    }

    private maxDynamicFieldValue() {
        const dynamicFieldValues = this.dynamicFields.map((df) => df.value).filter((v): v is number => v !== null);
        return dynamicFieldValues.length ? Math.max(...dynamicFieldValues) : 0;
    }

    private refreshParams() {
        if (this.$route.query.worksheetId) {
            this.worksheetId = parseInt(this.$route.query.worksheetId.toString());
        }

        if (this.$route.query.type) {
            this.type =
                this.$route.query.type === 'pm'
                    ? AppliedLessonType.PMAppliedLesson
                    : this.$route.query.type === 'x'
                    ? AppliedLessonType.XAppliedLesson
                    : AppliedLessonType.AppliedLesson;
        }

        if (this.$route.query.showResolved) {
            this.showResolved = this.$route.query.showResolved === 'true';
        }

        const fn = (response) => {
            this.worksheet = response.data;

            this.loadAppliedLessons().then(() => {
                if (this.$route.query.onlyPR) {
                    this.onlyPR = this.$route.query.onlyPR === 'true';
                }

                this.parseQueryFields(this.fields);

                const dynamicField: ExportField | undefined = this.fields.find(
                    (field) => field.field === 'dynamic_field'
                );
                if (dynamicField) {
                    dynamicField.header = () => this.companySetting.dynamic_worksheet_field_name + '<br>(R)';
                }

                document.title = 'Worksheet Report';

                this.loaded = true;
            });
        };

        switch (this.type) {
            case AppliedLessonType.PMAppliedLesson:
                pmWorksheetWebservice
                    .getById(this.worksheetId)
                    .then(fn)
                    .catch(() => {
                        showErrorNotification('toastMessages.error.worksheet');
                    });
                break;

            case AppliedLessonType.AppliedLesson:
                tradeWorksheetWebservice
                    .getById(this.worksheetId)
                    .then(fn)
                    .catch(() => {
                        showErrorNotification('toastMessages.error.worksheet');
                    });

                break;

            case AppliedLessonType.XAppliedLesson:
                xWorksheetWebservice
                    .getById(this.worksheetId)
                    .then(fn)
                    .catch(() => {
                        showErrorNotification('toastMessages.error.worksheet');
                    });
                break;
        }
    }

    private loadAppliedLessons() {
        const webservice =
            this.type === AppliedLessonType.PMAppliedLesson
                ? pmAppliedLessonWebservice
                : this.type === AppliedLessonType.XAppliedLesson
                ? xAppliedLessonWebservice
                : appliedLessonWebservice;

        return webservice
            .getByWorksheetId(this.worksheet.id)
            .then((response) => {
                this.applied_lessons = this.mapUsersRec(response.data);
                const haveSubLessons = !!this.applied_lessons.find((el) => el.sub_lessons.length > 0);
                if (!haveSubLessons) {
                    this.applied_lessons = this.sortDynamicField(this.applied_lessons, this.fields) as AppliedLesson[];
                } else {
                    this.applied_lessons = this.flatMap(this.applied_lessons);
                }
            })
            .catch(() => {
                showErrorNotification('toastMessages.error.worksheet');
            });
    }

    private flatMap(applied_lessons: any, flattened: any[] = [], levels: any[] = []) {
        for (let index = 0; index < applied_lessons.length; index++) {
            const applied_lesson = applied_lessons[index];
            const itemLevels = [...levels, index + 1];
            applied_lesson.lesson.insight = itemLevels.join('.') + ' - ' + applied_lesson.lesson.insight;
            flattened.push(applied_lesson);
            if (applied_lesson.sub_lessons) {
                this.flatMap(applied_lesson.sub_lessons, flattened, itemLevels);
            }
        }
        return flattened;
    }

    private mapUsersRec(lessons: any, mapped: [] = []) {
        mapped = lessons.map((lesson) => {
            lesson.responsible_user = findCombinedUser(this.users, lesson);
            lesson.contacttill = lesson.contacttill ? moment(lesson.contacttill).toDate() : null;
            if (lesson.sub_lessons) {
                lesson.sub_lessons = this.mapUsersRec(lesson.sub_lessons, mapped);
            }
            return lesson;
        });
        return mapped;
    }

    private loadProject() {
        const projectId = parseInt(this.$route.query.projectId.toString());
        projectWebservice
            .loadOneByUser(projectId)
            .then((response) => {
                this.project = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    get users(): CombinedUser[] {
        return getCombinedProjectUsers(this.project);
    }

    get transformed(): PrineConvertExportData {
        const clonedLessons = JSON.parse(JSON.stringify(this.applied_lessons));

        // check if none of the lessons needs to be checked
        let reducedActiveFields = this.activeFields(this.fields);
        let noChecksNeeded = true;
        for (const lesson of clonedLessons) {
            if (lesson.needscheck) {
                noChecksNeeded = false;
                break;
            }
        }
        if (noChecksNeeded) {
            reducedActiveFields = this.activeFields(this.fields).filter(
                (field) => !['needscheck', 'checked', 'checked_by_user'].includes(field.field)
            );
        }

        return [
            {
                fields: this.translatedFields(reducedActiveFields, {}) as any[],
                rows: clonedLessons
                    // showResolved
                    .filter((appliedLesson) => {
                        // if showResolved is true we display all completed and non-completed
                        if (this.showResolved) {
                            return true;
                        } else {
                            // Logic to check if a lesson is not completed
                            if (
                                (!appliedLesson.done && !appliedLesson.needscheck && !appliedLesson.checked) ||
                                (!appliedLesson.done && appliedLesson.needscheck) ||
                                (appliedLesson.done && appliedLesson.needscheck && !appliedLesson.checked)
                            ) {
                                return true;
                            }
                        }
                    })
                    .map((lesson) => {
                        return reducedActiveFields.map((field) =>
                            field.data
                                ? field.data({
                                      lesson,
                                      users: this.users,
                                  })
                                : ''
                        );
                    }),
            },
        ];
    }

    get htmlEntries() {
        const transformed = this.transformed;

        if (transformed && transformed.length > 0) {
            return PrineConvert.export(this.transformed).using(
                new PrineConvertAdapterHTML({
                    columnStyles: this.columnStyles(this.fields),
                })
            );
        }
    }

    get projectTitle() {
        if (!this.project) {
            return '';
        }

        if (this.type === AppliedLessonType.PMAppliedLesson) {
            return this.$t('keywords.inspectionCheckList') + ': ' + this.project.title;
        }

        if (this.type === AppliedLessonType.XAppliedLesson) {
            return this.$t('keywords.inspectionCheckList') + ': ' + this.project.title;
        }

        return this.$t('keywords.worksheet') + ': ' + this.project.title;
    }

    get worksheetTitle() {
        if (!this.worksheet) {
            return '';
        }

        if (this.type === AppliedLessonType.PMAppliedLesson && this.worksheet.pm_phase) {
            return this.$t('keywords.phase') + ': ' + this.worksheet.pm_phase.title;
        }

        if (this.type === AppliedLessonType.XAppliedLesson && this.worksheet.x_process) {
            return this.$t('keywords.phase') + ': ' + this.worksheet.x_process.title;
        }

        return this.$tc('keywords.trade', 1) + ': ' + this.worksheet.trade.title;
    }

    get phaseTitle() {
        if (!this.worksheet) {
            return '';
        }

        if (this.type === AppliedLessonType.PMAppliedLesson) {
            return undefined;
        }

        if (this.type === AppliedLessonType.XAppliedLesson) {
            return undefined;
        }

        return this.$t('keywords.phase') + ': ' + this.worksheet.phase.title;
    }

    // Example for CSV Export
    // noinspection JSUnusedLocalSymbols
    private toCSV() {
        PrineConvert.export(this.transformed)
            .using(new PrineConvertAdapterCSV())
            .forEach((container, index) => {
                const filename =
                    [
                        'appex',
                        'worksheet',
                        'export',
                        index + 1,
                        moment().format('DD-MM-YYYY'),
                        moment().format('HH-mm-ss'),
                    ].join('_') + '.csv';

                container.download(filename);
            });
    }
    get descriptions() {
        const descriptions: string[] = [];
        descriptions.push(this.worksheetTitle);
        if (this.phaseTitle) {
            descriptions.push(this.phaseTitle ? this.phaseTitle : '');
        }
        descriptions.push('Erstellt am: ' + moment().format('DD.MM.YYYY') + ' ' + moment().format('HH:mm:ss'));
        return descriptions;
    }
}
